/**
 * C.1 — Bank statement import schemas.
 *
 * Источники:
 *   - Kaspi Business XLSX  — выписка из Kaspi Gold личного кабинета.
 *   - Halyk XLSX           — выписка из Halyk Bank.
 *   - Forte XLSX           — выписка из ForteBank.
 *   - 1С «Клиент-Банк» TXT — стандартный текстовый формат обмена с 1С.
 *
 * Парсинг → `BankStatementRow[]` → matcher → `DraftPaymentCandidate[]` →
 * operator review → commit → `payments` с `source='bank_import'`, `state='draft'`.
 */

import { z } from 'zod';
import { Currency } from '../enums.js';
import { MoneyAmount } from '../primitives.js';

export const BankStatementFormat = z.enum([
  'kaspi_xlsx',
  'halyk_xlsx',
  'forte_xlsx',
  'onec_kb_txt',
]);
export type BankStatementFormat = z.infer<typeof BankStatementFormat>;

// B6 invariant — entity-уровень response-схемы forward-compat (см. zod-strict.test.ts).
// Server-side input validation — на api-contracts уровне.

export const BankStatementRow = z.object({
  raw_row_no: z.number().int().min(1),
  posting_date: z.string().date(),
  amount: MoneyAmount,
  direction: z.enum(['inflow', 'outflow']),
  description: z.string().min(1),
  /** БИН/ИИН в назначении платежа, если банк вытащил. */
  counterparty_bin: z
    .string()
    .regex(/^\d{12}$/)
    .nullable(),
  counterparty_name_raw: z.string(),
  counterparty_account: z.string().nullable(),
  /** Служебные поля из банка, полезные для аудита. */
  raw: z.record(z.unknown()),
});
export type BankStatementRow = z.infer<typeof BankStatementRow>;

export const BankStatementImportDraft = z.object({
  import_id: z.string().uuid(),
  filename: z.string().min(1),
  format: BankStatementFormat,
  currency: Currency,
  entity_id: z.string().uuid(),
  period_from: z.string().date(),
  period_to: z.string().date(),
  rows: z.array(BankStatementRow),
  parsed_at: z.string().datetime(),
});
export type BankStatementImportDraft = z.infer<typeof BankStatementImportDraft>;

export const DraftPaymentCandidate = z.object({
  bank_row_no: z.number().int().min(1),
  posting_date: z.string().date(),
  amount: MoneyAmount,
  direction: z.enum(['inflow', 'outflow']),
  description: z.string(),
  counterparty_id: z.string().uuid().nullable(),
  counterparty_bin_matched: z
    .string()
    .regex(/^\d{12}$/)
    .nullable(),
  counterparty_name_raw: z.string(),
  matched_counterparty_confidence: z.number().min(0).max(1),
  matched_invoice_ids: z.array(z.string().uuid()),
});
export type DraftPaymentCandidate = z.infer<typeof DraftPaymentCandidate>;

export const BankImportPreviewResponse = z.object({
  draft: BankStatementImportDraft,
  candidates: z.array(DraftPaymentCandidate),
  /** S-FIX-1-BE.C.1 — HMAC-SHA256(import_id+entity_id+sha256(rows)) для commit-replay-protection. */
  reconcile_token: z.string().min(1),
  expires_at: z.string().datetime(),
});
export type BankImportPreviewResponse = z.infer<typeof BankImportPreviewResponse>;

export const BankImportCommitBody = z.object({
  import_id: z.string().uuid(),
  /** S-FIX-1-BE.C.1 — обязательно при commit. */
  reconcile_token: z.string().min(1),
  format: BankStatementFormat,
  filename: z.string(),
  payments: z
    .array(
      z.object({
        bank_row_no: z.number().int().min(1),
        counterparty_id: z.string().uuid(),
        amount: MoneyAmount,
        direction: z.enum(['inflow', 'outflow']),
        posting_date: z.string().date(),
        description: z.string(),
      }),
    )
    .min(1),
});
export type BankImportCommitBody = z.infer<typeof BankImportCommitBody>;

export const BankImportCommitResponse = z.object({
  created_count: z.number().int().min(0),
  payment_ids: z.array(z.string().uuid()),
});
export type BankImportCommitResponse = z.infer<typeof BankImportCommitResponse>;
